import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CalendarDays, Mail, Moon, RefreshCw, ShieldAlert, StickyNote, Trophy } from 'lucide-react';
import { sendNightlySweepTest, useNightlySweep, type NightlySweepNote, type NightlySweepStatLine } from '@/hooks/useNightlySweep';
import { formatPlusMinus, formatSavePct, formatToi } from '@/lib/formatters';

export function NightlySweep() {
  const [params, setParams] = useSearchParams();
  const selectedDate = params.get('date');
  const sweep = useNightlySweep(selectedDate);
  const [sendStatus, setSendStatus] = useState<string | null>(null);
  const data = sweep.data;

  async function sendTest() {
    if (sendStatus === 'sending') return;
    setSendStatus('sending');
    try {
      const result = await sendNightlySweepTest(selectedDate);
      setSendStatus(result.skipped ? `Digest not sent: ${result.skipped}` : `Digest sent to ${result.sent} recipient${result.sent === 1 ? '' : 's'}.`);
    } catch (error) {
      setSendStatus(error instanceof Error ? error.message : 'Digest send failed.');
    }
  }

  return (
    <section className="space-y-5">
      <div className="command-panel overflow-hidden rounded-control border border-black/10 text-white">
        <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-bartlettGold">
              Nightly Sweep
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-normal">Last Night + Daily Brief</h1>
            <p className="mt-1 max-w-2xl text-sm text-white/70">
              End-of-night stat recap and Bartlett-entered client notes in one review surface.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex h-10 items-center gap-2 rounded-control border border-white/15 bg-white px-3 text-sm font-semibold text-bartlett">
              <CalendarDays size={15} />
              <input
                type="date"
                value={selectedDate ?? data?.date ?? ''}
                onChange={(event) =>
                  setParams(event.target.value ? new URLSearchParams({ date: event.target.value }) : new URLSearchParams(), {
                    replace: true,
                  })
                }
                className="bg-transparent font-mono outline-none"
              />
            </label>
            <button
              type="button"
              onClick={() => sweep.refetch()}
              className="focus-ring inline-flex h-10 items-center gap-2 rounded-control border border-white/15 bg-white px-3 text-sm font-semibold text-bartlett hover:bg-bartlettIce"
            >
              <RefreshCw size={15} />
              Reload
            </button>
            <button
              type="button"
              onClick={sendTest}
              className="focus-ring inline-flex h-10 items-center gap-2 rounded-control border border-bartlettGold bg-bartlettGold px-3 text-sm font-semibold text-bartlett hover:bg-white"
            >
              <Mail size={15} />
              Send digest
            </button>
          </div>
        </div>
        <div className="grid border-t border-white/10 bg-white/[0.04] sm:grid-cols-5">
          <Metric icon={Moon} label="Sweep date" value={data?.date ?? '—'} />
          <Metric icon={Trophy} label="Clients played" value={String(data?.totals.clientsPlayed ?? '—')} />
          <Metric icon={ShieldAlert} label="Flagged rows" value={String(data?.totals.flaggedRows ?? '—')} tone={(data?.totals.flaggedRows ?? 0) > 0 ? 'warn' : 'default'} />
          <Metric icon={StickyNote} label="Client notes" value={String(data?.totals.noteEntries ?? '—')} />
          <Metric icon={Mail} label="Email" value={emailStatus(data)} tone={data?.email.enabled ? 'default' : 'warn'} />
        </div>
      </div>

      {sendStatus && (
        <div className="rounded-control border border-border bg-white px-4 py-2 text-sm text-muted">
          {sendStatus}
        </div>
      )}

      {sweep.isLoading && <div className="h-40 animate-pulse rounded-control border border-border bg-white" />}
      {sweep.isError && (
        <div className="rounded-control border border-flag-critical/30 bg-flag-critical/5 p-4 text-sm text-flag-critical">
          Couldn&apos;t load the nightly sweep.
        </div>
      )}

      {data && (
        <>
          {!data.email.enabled && (
            <div className="flex gap-3 rounded-control border border-flag-warn/30 bg-flag-warn/10 px-4 py-3 text-sm text-ink">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-flag-warn" />
              <div>
                <p className="font-semibold">Digest email is wired but not enabled.</p>
                <p className="mt-1 text-muted">
                  Set `NIGHTLY_DIGEST_EMAIL_ENABLED=true`, `NIGHTLY_DIGEST_RECIPIENTS`, and the Cloudflare email binding to send this to Scott and Brian at 5am.
                </p>
              </div>
            </div>
          )}

          <div className="grid gap-5 xl:grid-cols-[1fr_0.9fr]">
            <Panel title="Top Stat Lines" empty={data.topPerformers.length === 0 ? 'No client stat lines found for this sweep date.' : null}>
              {data.topPerformers.map((line) => (
                <StatRow key={line.statLineId} line={line} />
              ))}
            </Panel>

            <Panel title="Bartlett Daily Brief" empty={data.notes.length === 0 ? 'No client notes, viewings, videos, or schedule entries were added during this day.' : null}>
              {data.notes.map((note) => (
                <NoteRow key={note.id} note={note} />
              ))}
            </Panel>
          </div>

          <div className="grid gap-5 xl:grid-cols-[0.9fr_1fr]">
            <Panel title="Flags To Review" empty={data.flagged.length === 0 ? 'No warning or critical stat flags for this sweep date.' : null}>
              {data.flagged.map((line) => (
                <StatRow key={line.statLineId} line={line} showFlags />
              ))}
            </Panel>

            <Panel title="League Rollup" empty={data.leagueGroups.length === 0 ? 'No league rows available.' : null}>
              <div className="grid gap-3 md:grid-cols-2">
                {data.leagueGroups.map((group) => (
                  <div key={group.league} className="rounded-control border border-border bg-bg p-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-mono text-[12px] font-semibold uppercase text-ink">{group.league}</div>
                      <div className="font-mono text-[11px] text-muted">{group.count} rows</div>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {group.lines.slice(0, 4).map((line) => (
                        <div key={`${group.league}-${line.statLineId}`} className="flex items-center justify-between gap-2 text-[13px]">
                          <span className="truncate text-ink">{line.playerName}</span>
                          <span className="shrink-0 font-mono text-muted">{line.goals ?? 0}-{line.assists ?? 0}-{line.points ?? 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          {data.goalieLines.length > 0 && (
            <Panel title="Goalies">
              {data.goalieLines.map((line) => (
                <StatRow key={line.statLineId} line={line} />
              ))}
            </Panel>
          )}
        </>
      )}
    </section>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  tone = 'default',
}: {
  icon: typeof Moon;
  label: string;
  value: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <div className="border-t border-white/10 px-4 py-3 sm:border-l sm:border-t-0">
      <div className="flex items-center gap-2 text-white/55">
        <Icon size={14} />
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em]">{label}</span>
      </div>
      <div className={['mt-1 truncate font-mono text-lg font-semibold', tone === 'warn' ? 'text-bartlettGold' : 'text-white'].join(' ')}>
        {value}
      </div>
    </div>
  );
}

function Panel({ title, empty, children }: { title: string; empty?: string | null; children?: React.ReactNode }) {
  return (
    <section className="rounded-control border border-border bg-white shadow-sm">
      <div className="border-b border-border bg-bartlettIce/60 px-4 py-3">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-ink">{title}</h2>
      </div>
      <div className="divide-y divide-border/70">
        {empty ? <div className="px-4 py-5 text-sm text-muted">{empty}</div> : children}
      </div>
    </section>
  );
}

function StatRow({ line, showFlags = false }: { line: NightlySweepStatLine; showFlags?: boolean }) {
  const opponent = line.opponent ? `${line.isHome === 0 ? '@' : 'vs'} ${line.opponent}` : null;
  const isGoalie = line.decision != null || line.saves != null;
  return (
    <Link to={`/people/${line.playerId}?tab=stats`} className="grid gap-3 px-4 py-3 text-sm hover:bg-bartlettIce/45 md:grid-cols-[minmax(180px,0.9fr)_1fr]">
      <div className="min-w-0">
        <div className="truncate font-semibold text-ink">{line.playerName}</div>
        <div className="mt-0.5 truncate font-mono text-[11px] text-muted">
          {[line.league, line.team, opponent, line.score].filter(Boolean).join(' · ')}
        </div>
        {showFlags && line.flags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {line.flags
              .filter((flag) => flag.status !== 'ok')
              .map((flag) => (
                <span key={`${line.statLineId}-${flag.flagType}`} className="rounded-full border border-flag-warn/40 bg-flag-warn/10 px-2 py-0.5 font-mono text-[10px] uppercase text-flag-warn">
                  {flag.flagType}
                </span>
              ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-5 gap-2">
        {isGoalie ? (
          <>
            <Mini label="DEC" value={line.decision ?? '—'} />
            <Mini label="SV" value={line.saves ?? '—'} />
            <Mini label="SA" value={line.shotsAgainst ?? '—'} />
            <Mini label="GA" value={line.goalsAgainst ?? '—'} />
            <Mini label="SV%" value={formatSavePct(line.savePct)} />
          </>
        ) : (
          <>
            <Mini label="G" value={line.goals ?? 0} />
            <Mini label="A" value={line.assists ?? 0} />
            <Mini label="P" value={line.points ?? 0} />
            <Mini label="SOG" value={line.shots ?? '—'} />
            <Mini label="+/-" value={formatPlusMinus(line.plusMinus)} />
          </>
        )}
      </div>
    </Link>
  );
}

function NoteRow({ note }: { note: NightlySweepNote }) {
  return (
    <Link to={`/people/${note.playerId}?tab=${note.type === 'note' ? 'timeline' : note.type === 'viewing' ? 'viewings' : note.type}`} className="block px-4 py-3 text-sm hover:bg-bartlettIce/45">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-ink">{note.playerName}</span>
        <span className="rounded-full border border-border bg-bg px-2 py-0.5 font-mono text-[10px] uppercase text-muted">
          {note.type}
        </span>
        <span className="font-mono text-[11px] text-muted">{addedTime(note.createdAt)}</span>
      </div>
      <div className="mt-1 font-semibold text-ink">{note.title}</div>
      {note.body && <p className="mt-1 whitespace-pre-wrap break-words text-[13px] leading-5 text-muted">{note.body}</p>}
    </Link>
  );
}

function Mini({ label, value }: { label: string; value: string | number }) {
  return (
    <span className="rounded-control border border-border/70 bg-bg px-2 py-1 text-right">
      <span className="block font-mono text-[9px] font-semibold uppercase text-muted">{label}</span>
      <span className="block font-mono text-[12px] font-semibold tabular-nums text-ink">{label === 'TOI' && typeof value === 'number' ? formatToi(value) : value}</span>
    </span>
  );
}

function addedTime(value: number): string {
  return new Date(value * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function emailStatus(data: ReturnType<typeof useNightlySweep>['data']): string {
  if (!data) return '—';
  if (!data.email.enabled) return 'off';
  if (!data.email.configured) return 'needs setup';
  return `${data.email.recipients.length} recipients`;
}
